import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { HeadingComponent } from '@uigy/shared/components/heading/heading.component';
import { UiSelectComponent } from '@uigy/shared/components/ui-inputs/ui-select/ui-select.component';
import { MenuItem } from '@uigy/shared/models/interfaces';
import { TimeFrame } from '@uigy/generated/graphql';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  WhatsNewKpiWidgetComponent,
} from '@uigy/modules/overview/kpis/kpi-whats-new/whats-new-kpi/whats-new-kpi-widget.component';
import {
  KpiWhatsNewICPConfigService,
} from '@uigy/modules/overview/kpis/kpi-whats-new/services/kpi-whats-new-icp-config.service';
import { TimeFrameItem, TimeFrameService } from '@uigy/shared/services/time-frame.service';
import { WhatsNewKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import {
  NetworkGraphComponent,
} from '@uigy/shared/components/widgets/network-graph/network-graph.component';
import { IemService } from '@uigy/shared/services/iem.service';
import {
  KpiWhatsNewEMConfigService,
} from '@uigy/modules/overview/kpis/kpi-whats-new/services/kpi-whats-new-em-config.service';

@Component({
    selector: 'app-whats-new-kpi-wrapper',
    imports: [
        HeadingComponent,
        UiSelectComponent,
        WhatsNewKpiWidgetComponent,
        NetworkGraphComponent,
    ],
    templateUrl: './whats-new-kpi-wrapper.component.html',
    styleUrl: './whats-new-kpi-wrapper.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhatsNewKpiWrapperComponent implements OnInit {
  public timeframeItems: MenuItem<TimeFrameItem>[] = [];
  public kpiConfigs: WhatsNewKpiConfig[] = [];

  public selectedTimeFrame = signal<TimeFrameItem | undefined>(undefined);
  public title?: string;

  private languageService = inject(LanguageService);
  private timeFrameService = inject(TimeFrameService);
  private kpiWhatsNewICPConfigService = inject(KpiWhatsNewICPConfigService);
  private kpiWhatsNewEMConfigService = inject(KpiWhatsNewEMConfigService);
  private iemService = inject(IemService);

  public readonly isEM = signal<boolean>(this.iemService.isIEM());
  private readonly DEFAULT_TIME_FRAME = TimeFrame.OneWeek;

  constructor() {
  }

  public ngOnInit(): void {
    this.setInitialState();
  }

  public handleChange(value: TimeFrameItem): void {
    if (this.isEM()) {
      return;
    }

    this.selectedTimeFrame.set(value);
    this.updateKpiConfigs();
  }

  private setInitialState(): void {
    this.isEM() ? this.setupEMConfigs() : this.setupICPConfigs();
  }

  private setupICPConfigs(): void {
    this.title = this.languageService.texts.overview.kpi_whats_new_widgets.title;
    this.timeframeItems = this.timeFrameService.mapTimeFramesToMenuItems(this.DEFAULT_TIME_FRAME);

    const selectedTimeFrame = this.timeframeItems.find(item => item.selected) || this.timeframeItems[0];
    if (selectedTimeFrame) {
      selectedTimeFrame.selected = true;
      this.selectedTimeFrame.set(selectedTimeFrame.value);
    }

    this.kpiConfigs = this.kpiWhatsNewICPConfigService.getConfigs();
    this.updateKpiConfigs();
  }

  private setupEMConfigs(): void {
    this.title = this.languageService.texts.overview.kpi_whats_new_widgets.em.title;
    this.kpiConfigs = this.kpiWhatsNewEMConfigService.getConfigs();
  }

  private updateKpiConfigs(): void {
    const timeFrameValue = this.selectedTimeFrame();
    if (!timeFrameValue) {
      return;
    }

    this.kpiConfigs = this.kpiConfigs.map(config => ({
      ...config,
      timeFrameValue: {...timeFrameValue}
    }));
  }
}
